import { DateTime } from 'luxon'
import { makeAutoObservable } from 'mobx'
import Consumption from 'src/models/Consumption'
import apiClient from 'src/utils/apiClient'
import {
  EVConsumptionParams,
  DailyParams as _DailyParams,
  HourlyParams as _HourlyParams,
  MonthlyParams as _MonthlyParams,
  dailyDateFormat,
  hourlyDateFormat,
  monthlyDateFormat,
} from 'src/utils/apiClient/consumptions'

type DailyParams = Omit<_DailyParams, 'fromDate' | 'toDate'> & {
  fromDate: DateTime
  toDate: DateTime
}
type HourlyParams = Omit<_HourlyParams, 'fromDate' | 'toDate'> & {
  fromDate: DateTime
  toDate: DateTime
}
type MonthlyParams = Omit<_MonthlyParams, 'fromDate' | 'toDate'> & {
  fromDate: DateTime
  toDate: DateTime
}
type CacheKeyParams = { fromDate: DateTime; toDate: DateTime }

const CACHE_SIZE = 10

export default class ConsumptionStore {
  private caches = {
    daily: new Map<string, Consumption>(),
    ev: new Map<string, number | null>(),
    hourly: new Map<string, Consumption>(),
    monthly: new Map<string, Consumption>(),
  }

  // Constructor ------------

  constructor() {
    makeAutoObservable(this)
  }

  // Actions ---------------

  getDailyConsumptions = async (params: DailyParams) => {
    const cacheKey = this.cacheKey(params)
    const cache = this.caches.daily.get(cacheKey)
    if (cache) return cache

    const res = await apiClient.consumptions.getDailyConsumptions({
      ...params,
      fromDate: params.fromDate.toFormat(dailyDateFormat),
      toDate: params.toDate.toFormat(dailyDateFormat),
    })

    const data = new Consumption(res)
    this.caches.daily.set(cacheKey, data)

    // キャッシュサイズを超えた場合、最も古いキャッシュを削除
    if (this.caches.daily.size > CACHE_SIZE) this.caches.daily.delete(this.caches.daily.keys().next().value)

    return data
  }

  getEVConsumption = async (params: EVConsumptionParams) => {
    const cacheKey = `${params.year}-${params.month}`
    const cache = this.caches.ev.get(cacheKey)
    if (cache !== void 0) return cache

    const { consumption } = await apiClient.consumptions.getEVConsumption(params)

    this.caches.ev.set(cacheKey, consumption)

    // キャッシュサイズを超えた場合、最も古いキャッシュを削除
    if (this.caches.ev.size > CACHE_SIZE) this.caches.ev.delete(this.caches.ev.keys().next().value)

    return consumption
  }

  getHourlyConsumptions = async (params: HourlyParams) => {
    const cacheKey = this.cacheKey(params)
    const cache = this.caches.hourly.get(cacheKey)
    if (cache) return cache

    const res = await apiClient.consumptions.getHourlyConsumptions({
      ...params,
      fromDate: params.fromDate.toFormat(hourlyDateFormat),
      toDate: params.toDate.toFormat(hourlyDateFormat),
    })

    const data = new Consumption(res)
    this.caches.hourly.set(cacheKey, data)

    // キャッシュサイズを超えた場合、最も古いキャッシュを削除
    if (this.caches.hourly.size > CACHE_SIZE) this.caches.hourly.delete(this.caches.hourly.keys().next().value)

    return data
  }

  getMonthlyConsumptions = async (params: MonthlyParams) => {
    const cacheKey = this.cacheKey(params)
    const cache = this.caches.monthly.get(cacheKey)
    if (cache) return cache

    const res = await apiClient.consumptions.getMonthlyConsumptions({
      ...params,
      fromDate: params.fromDate.toFormat(monthlyDateFormat),
      toDate: params.toDate.toFormat(monthlyDateFormat),
    })

    const data = new Consumption(res)
    this.caches.monthly.set(cacheKey, data)

    // キャッシュサイズを超えた場合、最も古いキャッシュを削除
    if (this.caches.monthly.size > CACHE_SIZE) this.caches.monthly.delete(this.caches.monthly.keys().next().value)

    return data
  }

  // Private actions ---------------

  cacheKey = ({ fromDate, toDate }: CacheKeyParams) => {
    const format = 'yyyyMMddHHmmss'
    return `${fromDate.toFormat(format)}-${toDate.toFormat(format)}`
  }
}
